import * as React from 'react';
import { Dispatch } from 'react-redux';
import { StoreState } from '../../reducers';
import Utils from '../../utils';
import MultiAppAuthenticationPopup from '../../containers/MultiAppAuthentication/Popup';
import Loader, { LoaderType, LoaderStepName, LoaderStep } from '../Loader';
import MultiAppAuthenticationForm from '../../containers/MultiAppAuthentication/Form';
import { MultiAppAuthenticationCredentials } from '../../containers/MultiAppAuthentication/reducer';
import {
	StyledMultiAppAuthentication, MultiAppAuthenticationTitle, WebpublicationLogo, SwitchToDemoViewBlock, SwitchLoginViewBlock,
	BackLink, SwitchToDemoFormButton, MultiAppAuthenticationContainer, FormWrapper, MultiAppAuthenticationMethodButton, MultiAppAuthenticationMethods
} from '../../components/MultiAppAuthentication/styles';
import { AUTH_METHODS, MultiAppAuthenticationViewMode, AuthMethod } from '../../containers/MultiAppAuthentication/constants';
import { Translate } from 'react-redux-i18n';
import { orgLogin } from '../../containers/MultiAppAuthentication/actions';
import LanguageSwitcher from '../../containers/LanguageSwitcher';
import AuthUtils from '../../utils/authentication';
import { Api } from '../../typings/api';

export interface MultiAppAuthenticationProps {
	accessibleApps: Api.AspDriveResourceItem[];
	credentials: MultiAppAuthenticationCredentials;
	galleryConfig: Api.Config;
	orgAuthMethods: AuthMethod[];
	login: string;
	userLocale: string; // do not remove. Prop is used to update locale through rerender
	dispatch: Dispatch<StoreState>;
}

interface MultiAppAuthenticationState {
	isServerSelect: boolean;
	isSelectPopupOpen: boolean;
	shouldRememberCredentials: boolean;
	viewMode: MultiAppAuthenticationViewMode;
	hasLoader: boolean;
	method?: AuthMethod;
}

export class MultiAppAuthentication extends React.Component<MultiAppAuthenticationProps, MultiAppAuthenticationState> {

	logoClickCount = 0;

	constructor(props: MultiAppAuthenticationProps) {
		super(props);
		const { accessibleApps } = props;

		this.state = {
			isSelectPopupOpen: accessibleApps.length > 1,
			isServerSelect: false,
			shouldRememberCredentials: true,
			viewMode: MultiAppAuthenticationViewMode.Login,
			hasLoader: false
		};

	}

	componentDidUpdate(prevProps: MultiAppAuthenticationProps, prevState: MultiAppAuthenticationState) {
		const { viewMode } = this.state;
		const { credentials, galleryConfig, accessibleApps, dispatch } = this.props;
		const company = credentials && credentials.company || '';

		if (prevProps.accessibleApps !== accessibleApps) {
			this.setState({ isSelectPopupOpen: accessibleApps.length > 1 });
		}
		if (
			!this.isOrgLoginView(prevState.viewMode) &&
			this.isOrgLoginView(viewMode)
		) {
			dispatch(orgLogin(galleryConfig, company));
		}
	}

	onPopupClosed = (): void => {
		this.setState({ isSelectPopupOpen: false, isServerSelect: false });
	}

	handleLogoClick = (): void => {
		this.logoClickCount++;
		if (this.logoClickCount > 9) {
			this.setState({ isSelectPopupOpen: true, isServerSelect: true });
			this.logoClickCount = 0;
		}
	}

	selectAuthMethod(method: AuthMethod): void {
		this.setState({ method });

		if (method === AuthMethod.Webpublication) {
			return this.switchToLoginView();
		} else {
			return this.switchToOrgLoginView();
		}
	}

	switchToDemoView = (): void => {
		this.switchViewMode(MultiAppAuthenticationViewMode.Creation);
	}

	switchToLoginView = (): void => {
		this.switchViewMode(MultiAppAuthenticationViewMode.Login);
	}

	switchToCompanyView = (): void => {
		this.switchViewMode(MultiAppAuthenticationViewMode.Company);
	}

	switchToOrgLoginView(): void {
		this.switchViewMode(MultiAppAuthenticationViewMode.OrgLogin);
	}

	switchViewMode = (viewMode: MultiAppAuthenticationViewMode): void => {
		this.setState({ viewMode });
	}

	setLoader = (hasLoader: boolean): void => {
		this.setState({ hasLoader });
	}

	isLoginView = (viewMode?: MultiAppAuthenticationViewMode): boolean => {
		return AuthUtils.isLoginView(viewMode || this.state.viewMode);
	}

	isOrgLoginView = (viewMode?: MultiAppAuthenticationViewMode): boolean => {
		return AuthUtils.isOrgLoginView(viewMode || this.state.viewMode);
	}

	isPasswordView = (viewMode?: MultiAppAuthenticationViewMode): boolean => {
		return AuthUtils.isPasswordView(viewMode || this.state.viewMode);
	}

	isCompanyView = (viewMode?: MultiAppAuthenticationViewMode): boolean => {
		return AuthUtils.isCompanyView(viewMode || this.state.viewMode);
	}

	isMethodView = (viewMode?: MultiAppAuthenticationViewMode): boolean => {
		return AuthUtils.isMethodView(viewMode || this.state.viewMode);
	}

	isCreationView = (viewMode?: MultiAppAuthenticationViewMode): boolean => {
		return AuthUtils.isCreationView(viewMode || this.state.viewMode);
	}

	get viewTextTitle(): string {
		const { isLoginView, isCompanyView, isMethodView, isPasswordView } = this;

		if (isCompanyView()) {
			return 'messages.your_company_account';
		} else if (isLoginView() || isMethodView()) {
			return 'messages.or_sign_in';
		} else if (isPasswordView()) {
			return 'messages.enter_password';
		}

		return '';
	}

	renderMethodButtons = () => {
		const { orgAuthMethods } = this.props;

		return orgAuthMethods.map(method => {
			return (
				<MultiAppAuthenticationMethodButton onClick={() => this.selectAuthMethod(method)} key={`button-${method}`}>
					<Translate value={`${AUTH_METHODS[method].text}`} />
				</MultiAppAuthenticationMethodButton>
			);
		});
	}

	renderBackButtonBlock() {
		const { isMethodView, isCreationView, isPasswordView } = this;

		return (
			<SwitchLoginViewBlock isCreationView={isCreationView()} onClick={isMethodView() ? this.switchToCompanyView : this.switchToLoginView}>
				<BackLink>{Utils.getSvgIcon('iconset_back')}
					{isPasswordView()
						? this.props.login
						: <Translate value={`messages.back_to_${isMethodView() ? 'company' : 'login'}`} />
					}
				</BackLink>
			</SwitchLoginViewBlock>
		);
	}

	renderAuthenticationBlock() {
		const { isMethodView, isOrgLoginView, isLoginView } = this;
		const { viewMode } = this.state;
		const showBackButton = !isLoginView() && !isOrgLoginView();

		return (
			<>
				<LanguageSwitcher />
				<MultiAppAuthenticationContainer>
					<WebpublicationLogo onClick={this.handleLogoClick}>{Utils.getSvgIcon('uprez')}</WebpublicationLogo>
					<FormWrapper>
						{isLoginView() &&
							<SwitchToDemoViewBlock>
								<SwitchToDemoFormButton onClick={this.switchToDemoView}>
									<Translate value="messages.test_our_demo" />
								</SwitchToDemoFormButton>
							</SwitchToDemoViewBlock>
						}
						{showBackButton &&
							this.renderBackButtonBlock()
						}
						{this.viewTextTitle &&
							<MultiAppAuthenticationTitle>
								<Translate value={this.viewTextTitle} />
							</MultiAppAuthenticationTitle>
						}
						{!isMethodView() ?
							<MultiAppAuthenticationForm
								viewMode={viewMode}
								switchViewMode={this.switchViewMode}
								setLoader={this.setLoader}
							/>
							: <MultiAppAuthenticationMethods>
								{this.renderMethodButtons()}
							</MultiAppAuthenticationMethods>
						}
					</FormWrapper>
				</MultiAppAuthenticationContainer>
			</>
		);
	}

	render() {
		const { isSelectPopupOpen, shouldRememberCredentials, hasLoader, isServerSelect } = this.state;
		const { isOrgLoginView, isCreationView, isPasswordView } = this;
		const showLoader = hasLoader && (isCreationView() || isPasswordView());
		const loaderStep: LoaderStep = isCreationView() ? { step: LoaderStepName.Creating } : { step: LoaderStepName.LoggingIn };

		return (
			<StyledMultiAppAuthentication>
				{showLoader &&
					<Loader loaderStep={loaderStep} type={LoaderType.Overlay} />
				}
				{isSelectPopupOpen &&
					<MultiAppAuthenticationPopup
						onClose={this.onPopupClosed}
						shouldSaveGalleryConfig={shouldRememberCredentials}
						isServerSelect={isServerSelect}
					/>
				}
				{!isOrgLoginView() && this.renderAuthenticationBlock()}
			</StyledMultiAppAuthentication>
		);
	}
}

export default MultiAppAuthentication;



// WEBPACK FOOTER //
// ./node_modules/tslint-loader!./src/components/MultiAppAuthentication/index.tsx